import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { SSOApi } from '../../axios/sso';
import { useLanguage } from '../../hooks';
import { useUserDataStore } from '../../newStore/userDataStore';
import { SSODiscoveryResponse, SSOStatusResponse } from '../../types';

interface SSOAuthContextType {
  isSSOAuthenticated: boolean;
  ssoUser: SSOStatusResponse | null;
  isLoading: boolean;
  error: string | null;
  discoveryResult: SSODiscoveryResponse | null;
  cognitoConfig: {
    authority: string;
    client_id: string;
    redirect_uri: string;
    response_type: string;
    scope: string;
  } | null;
  discoverAndLogin: (identifier: string) => Promise<void>;
  logoutFromSSO: () => Promise<void>;
  checkSSOAuthStatus: () => Promise<void>;
  setSSOError: (error: string | null) => void;
  handleProviderLogin: (provider: {
    id: string;
    name: string;
    type: string;
    display_name: string;
    icon: string;
    auth_flow: string;
    authorization_url: string;
  }) => Promise<void>;
}

const SSOAuthContext = createContext<SSOAuthContextType | undefined>(undefined);

interface SSOAuthProviderProps {
  children: ReactNode;
}

export function SSOAuthProvider({ children }: SSOAuthProviderProps) {
  const [isSSOAuthenticated, setIsSSOAuthenticated] = useState(false);
  const [ssoUser, setSsoUser] = useState<SSOStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discoveryResult, setDiscoveryResult] = useState<SSODiscoveryResponse | null>(null);
  const [cognitoConfig, setCognitoConfig] = useState<{
    authority: string;
    client_id: string;
    redirect_uri: string;
    response_type: string;
    scope: string;
  } | null>(null);

  const { actions: userActions } = useUserDataStore();
  const lang = useLanguage();
  const oidcAuth = useAuth();

  const getErrorMessage = (errorCode?: string) => {
    switch (errorCode) {
      case 'TOO_MANY_ATTEMPTS':
        return lang.login.ssoTooManyAttempts;
      case 'INVALID_IDENTIFIER':
        return lang.login.ssoInvalidIdentifier;
      case 'FAILED_VALIDATION':
        return lang.login.ssoFailedValidation;
      case 'FAILED_STATUS_CHECK':
        return lang.login.ssoFailedStatus;
      default:
        return errorCode || lang.login.ssoInvalidIdentifier;
    }
  };

  const checkSSOAuthStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const status = await SSOApi.checkSSOStatus();

      if (status.authenticated) {
        setIsSSOAuthenticated(true);
        setSsoUser(status);
        await userActions.initializeUserData();
      } else {
        setIsSSOAuthenticated(false);
        setSsoUser(null);
      }
    } catch (err) {
      console.error('Failed to check SSO status:', err);
      setError(lang.login.ssoFailedStatus);
      setIsSSOAuthenticated(false);
      setSsoUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const discoverAndLogin = async (identifier: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setDiscoveryResult(null);
      setCognitoConfig(null);

      const discovery = await SSOApi.discoverSSOPool(identifier);

      if (discovery.success && 'providers' in discovery && discovery.providers) {
        setDiscoveryResult(discovery);

        // Generate Cognito configuration from discovery response
        if (discovery.cognito_config && discovery.pool_info) {
          const cognitoConfig = {
            authority: `https://cognito-idp.${discovery.cognito_config.region}.amazonaws.com/${discovery.cognito_config.pool_id}`,
            client_id: discovery.pool_info.client_id,
            redirect_uri: discovery.cognito_config.redirect_uri,
            response_type: 'code',
            scope: 'email openid phone profile',
          };
          setCognitoConfig(cognitoConfig);
        }
      } else if (!discovery.success && 'error' in discovery) {
        setError(getErrorMessage(discovery.error));
      }

      setIsLoading(false);
      return;
    } catch (err) {
      console.error('SSO discovery/login failed:', err);
      setError(lang.login.ssoFailedInitiate);
      setIsLoading(false);
    }
  };

  const logoutFromSSO = async () => {
    try {
      await SSOApi.logoutSSO();
      setIsSSOAuthenticated(false);
      setSsoUser(null);
      userActions.logoutUser();
    } catch (err) {
      console.error('Failed to logout from SSO:', err);
      setError(lang.login.logoutError);
    }
  };

  const handleProviderLogin = async (provider: {
    id: string;
    name: string;
    type: string;
    display_name: string;
    icon: string;
    auth_flow: string;
    authorization_url: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if we have Cognito configuration available
      if (!cognitoConfig) {
        setError('SSO configuration not available. Please try again.');
        return;
      }

      // Handle different auth flows based on provider type
      if (provider.type === 'oidc' || provider.type === 'saml') {
        if (oidcAuth && !oidcAuth.isAuthenticated) {
          await oidcAuth.signinRedirect();
        }
      } else if (provider.authorization_url) {
        // For other provider types, redirect to authorization URL
        window.location.href = provider.authorization_url;
      } else {
        setError('Authorization URL not available for this provider');
      }
    } catch (err) {
      console.error('Provider login failed:', err);
      setError('Failed to initiate login with selected provider');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkSSOAuthStatus();
  }, []);

  const value: SSOAuthContextType = {
    isSSOAuthenticated,
    ssoUser,
    isLoading,
    error,
    discoveryResult,
    cognitoConfig,
    discoverAndLogin,
    logoutFromSSO,
    checkSSOAuthStatus,
    setSSOError: setError,
    handleProviderLogin,
  };

  return <SSOAuthContext.Provider value={value}>{children}</SSOAuthContext.Provider>;
}

export const useSSOAuthContext = () => useContext(SSOAuthContext);
